import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { toast } from 'sonner';
import {
	DefaultRichContentRawCSS,
	ILocationState,
	IModalContext,
	ModalChildComponentContextKey,
} from '../../../../models';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { ToolbarBlog } from '../../../../models/UiUtils';
import { useBlogTemplateCategoryNames, useGetSystemJobQuery } from '../../../../queries';
import { TemplateCategoriesViewModel, TemplateWithStatusesViewModel } from '../../../../viewmodels/AdminViewModels';
import { TextArea } from '../../../../web/components-v2/TextArea';
import { ImageFileChooserModal } from '../../../../web/components/ImageFileChooserModal';
import { ImagePreview } from '../../../../web/components/ImagePreview';
import { ImageUploadButtons } from '../../../../web/components/ImageUploadButtons';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { MediaChooserModal } from '../../../../web/components/MediaChooser';
import { IModalProps } from '../../../../web/components/Modal';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import {
	IRichContentDocumentEditorConfig,
	RichContentDocumentEditor,
} from '../../../../web/components/richContent/RichContentDocumentEditor';
import { ISelectBoxOption } from '../../../../web/components/SelectBox';
import { RefreshIcon } from '../../../../web/components/svgs/icons/RefreshIcon';
import { TextInput } from '../../../../web/components/TextInput';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { IndustryCategorySection } from '../IndustryCategorySection';
import { ImageStage, REDUCER_TYPE_KEYS, useEditBlogPostTemplate, useEditBlogQueries } from './hooks';
import { BlogPostContentPreview } from './presentation';
import { styleSheet } from './styles';

const defaultEditorConfig: IRichContentDocumentEditorConfig = {
	autoresizeToFitContent: false,
	contentHorizontalPadding: 20,
	contentRawCss: DefaultRichContentRawCSS,
	minHeight: 100,
	plugins: 'autolink',
	toolbar: ToolbarBlog,
};
const defaulReadOnlyEditorConfig: IRichContentDocumentEditorConfig = {
	...defaultEditorConfig,
	plugins: [],
	toolbar: false,
};

interface IProps extends IModalContext {
	className?: string;
}

export const _EditBlogPostTemplate = ({ className, parentModal }: IProps) => {
	const { logApiError } = useEventLogging('EditBlogPostTemplate');
	const errorMessages = useErrorMessages();
	const params = useParams<{ id: string }>();
	const loc = useLocation();
	const history = useHistory();
	const { state, dispatch } = useEditBlogPostTemplate();

	const onFreeImageChooserRequestCloseRef =
		React.useRef<(img?: Api.IFileAttachmentWithURL, cancel?: boolean) => void>(null);
	const [freeImageModalProps, setFreeImageModalProps] = React.useState<IModalProps<Api.IFileAttachmentWithURL>>({
		isOpen: false,
		onRequestClose: (result, cancel) => onFreeImageChooserRequestCloseRef.current?.(result, cancel),
	});
	const isReadOnly = React.useMemo(() => loc?.search?.includes('readonly'), [loc]);
	const isSystemJobQueryEnabled = Boolean(state.jobId);
	const hasImageSelected = Boolean(state.selectedImage);

	const locationState: ILocationState<TemplateCategoriesViewModel, TemplateWithStatusesViewModel> =
		history.location.state;

	const templateId = params.id === 'new' ? '' : params.id;

	const modalContent = React.useMemo(() => {
		return state.content?.document?.replace(/<[^>]*>?/gm, '');
	}, [state.content]);

	const {
		blogTemplateQuery,
		addStatusesMutation,
		deleteStatusesMutation,
		updateStatusesMutation,
		blogGenerationMutation,
		blogMutation,
		blogUpdateMutation,
		imageAttachmentMutation,
	} = useEditBlogQueries({ templateId, updateState: dispatch, handleBlogUpdateComplete: onFinish });

	const blogCategoriesQuery = useBlogTemplateCategoryNames();

	const systemJobQuery = useGetSystemJobQuery<Api.IBlogGenerationSystemJob>({
		enabled: isSystemJobQueryEnabled,
		jobId: state.jobId,
		refetchInterval: query => {
			const data = query.state.data;
			if (!state.jobId) {
				return false;
			}
			return !data?.generatedContent ? 3000 : false;
		},
		refetchOnWindowFocus: false,
	});
	React.useEffect(() => {
		if (systemJobQuery.data) {
			if (systemJobQuery.data.generatedContent) {
				dispatch({
					type: REDUCER_TYPE_KEYS.CONTENT_GENERATED_SUCCESSFULLY,
					payload: systemJobQuery.data,
				});
			}
			if (systemJobQuery.data.percentComplete === 100 && !systemJobQuery.data.generatedContent) {
				toast.error('Blog generation failed');
				dispatch({ type: REDUCER_TYPE_KEYS.BLOG_GENERATION_FAILED });
			}
		}
	}, [systemJobQuery.data, dispatch]);

	const generateContent = () => {
		dispatch({ type: REDUCER_TYPE_KEYS.CONTENT_GENERATE_CLICKED });
		blogGenerationMutation.mutate({ template: { prompt: state.promptInput.getRawRichTextContent() } });
	};

	React.useEffect(() => {
		if (state.content && !state.selectedImage && state.imageState === ImageStage.Loaded) {
			toast.success('Select a blog image');
			onClickFreeImage();
		}
	}, [state.content, state.imageState, state.selectedImage]);

	React.useEffect(() => {
		if (!hasImageSelected && state.imageState === ImageStage.Loading) {
			dispatch({
				type: REDUCER_TYPE_KEYS.IMAGE_STATE_LOADED,
				payload: {
					imageState: ImageStage.Loaded,
					imageSuggestingLoading: false,
				},
			});
		}
	}, [hasImageSelected, state.imageState, dispatch]);

	React.useEffect(() => {
		if (blogTemplateQuery.data && templateId) {
			dispatch({
				type: REDUCER_TYPE_KEYS.BLOG_TEMPLATE_DATA_LOADED_SUCCESSFULLY,
				payload: blogTemplateQuery.data,
			});
		}
	}, [blogTemplateQuery.data, dispatch, templateId]);

	const onFreeImageChooserRequestClose = React.useCallback(
		(imageFile?: Api.IFileAttachmentWithURL, cancel?: boolean) => {
			setFreeImageModalProps(value => {
				return {
					...value,
					isOpen: false,
				};
			});
			if (cancel) {
				dispatch({
					type: REDUCER_TYPE_KEYS.IMAGE_STATE_LOADED,
					payload: {
						imageState: ImageStage.Loaded,
						imageSuggestingLoading: false,
					},
				});
				return;
			}

			dispatch({
				type: REDUCER_TYPE_KEYS.IMAGE_SUCCESSFULLY_UPLOADED,
				payload: imageFile,
			});
		},
		[dispatch]
	);

	onFreeImageChooserRequestCloseRef.current = onFreeImageChooserRequestClose;

	const handleFileInputModalClose = (val?: File | null, cancel?: boolean) => {
		dispatch({ type: REDUCER_TYPE_KEYS.CLOSE_INPUT_MODAL });
		if (val && !cancel) {
			imageAttachmentMutation.mutate({ files: [val] });
		}
	};
	function onClickFreeImage() {
		setFreeImageModalProps(value => {
			return {
				...value,
				isOpen: true,
			};
		});
	}

	const onClickMedia = () => {
		dispatch({ type: REDUCER_TYPE_KEYS.OPEN_INPUT_MODAL });
	};

	const updateSelectionsStatuses = (statuses: Api.ITemplateStatus[], multipleSelection = false) => {
		dispatch({
			type: REDUCER_TYPE_KEYS.INDUSTRY_OPTIONS_CHANGED,
			payload: { statuses, updateMultiple: multipleSelection },
		});
	};

	const onCheckboxChanged = (industry: Api.Industry) => (e: React.ChangeEvent<HTMLInputElement>) => {
		const newSelectedIndustryOptions = state.selectedIndustryOptions.filter(x => x.industry !== industry);
		if (e.target.checked) {
			newSelectedIndustryOptions.push({ industry, category: '' });
		}

		updateSelectionsStatuses(newSelectedIndustryOptions);
	};

	const onSelectBoxChanged = (industry: Api.Industry) => (selectedOption: ISelectBoxOption<string>) => {
		/**
		 * @TODO take a look at where there is an issue with multiple selections
		 */
		// if (!state.multipleSelection) {
		// 	const multipleStatuses: Api.ITemplateStatus[] = getCategoriesByName(
		// 		blogCategoriesQuery.data,
		// 		selectedOption.value
		// 	);

		// 	updateSelectionsStatuses(multipleStatuses, multipleStatuses.length > 1);
		// 	return;
		// }
		const newSelectedIndustryOptions = state.selectedIndustryOptions.filter(x => x.industry !== industry);
		newSelectedIndustryOptions.push({
			category: selectedOption.value,
			industry,
		});

		updateSelectionsStatuses(newSelectedIndustryOptions);
	};

	const resetImage = () => {
		dispatch({ type: REDUCER_TYPE_KEYS.RESET_IMAGE_PREVIEW });
	};

	const validate = (): boolean => {
		const errors: string[] = [];
		if (!state.promptInput) {
			errors.push('Writing Instructions for the AI is required');
		}
		if (!state.selectedImage) {
			errors.push('Image is required');
		}
		if (!state.selectedIndustryOptions || state.selectedIndustryOptions.length === 0) {
			errors.push('At least one industry is required');
		}
		if (!state.titleInput) {
			errors.push('Summary title is required');
		}
		if (!state.summaryInput) {
			errors.push('Summary is required');
		}
		if (errors.length) {
			errorMessages.push({
				messages: errors,
			});
			return false;
		}
		return errors.length === 0;
	};

	const createTemplate = (published?: boolean) => {
		const template: Api.IBlogTemplate = {
			prompt: state.promptInput.getRawRichTextContent(),
			keywords: state.keywordsInput?.split(',')?.map(x => x.trim()),
			summary: state.summaryInput,
			name: state.titleInput,
			attachments: [state.selectedImage],
			templateType: Api.TemplateType.Blog,
		};
		const statuses: Api.ITemplateStatus[] = [];
		const statusesToRemove: Api.ITemplateStatus[] = [];
		const statusesToAdd: Api.ITemplateStatus[] = [];
		const statusesToUpdate: Api.ITemplateStatus[] = [];
		state.selectedIndustryOptions.forEach(o => {
			statuses.push({
				category: o.category,
				industry: o.industry,
				status: published || state.isPublished ? Api.TemplateStatus.Published : Api.TemplateStatus.Draft,
			});
		});

		if (state.isEdit) {
			blogCategoriesQuery.data.forEach(o => {
				if (o?.categoryNames?.length > 0) {
					const startStatusForIndustry = state.initialIndustryOptions.filter(x => x.industry === o.industry)[0];

					const endStatusForIndustry = state.selectedIndustryOptions.filter(x => x.industry === o.industry)[0];

					if (!startStatusForIndustry && endStatusForIndustry) {
						statusesToAdd.push({
							...endStatusForIndustry,
							status: Api.TemplateStatus.Published,
						});
					} else if (!endStatusForIndustry && startStatusForIndustry) {
						statusesToRemove.push(startStatusForIndustry);
					} else if (startStatusForIndustry?.category !== endStatusForIndustry?.category) {
						statusesToRemove.push(startStatusForIndustry);
						statusesToAdd.push({
							...endStatusForIndustry,
							status: Api.TemplateStatus.Published,
						});
					} else if (
						startStatusForIndustry?.status !== endStatusForIndustry?.status ||
						endStatusForIndustry?.status === Api.TemplateStatus.Draft
					) {
						statusesToUpdate.push({
							...endStatusForIndustry,
							status: Api.TemplateStatus.Published,
						});
					}
				}
			});
		}

		return { template, statuses, statusesToRemove, statusesToAdd, statusesToUpdate };
	};

	const handleSave = (published = false): void => {
		if (!validate()) {
			return;
		}
		if (!state.isEdit) {
			const _template: { template: Api.IBlogTemplate; statuses: Api.ITemplateStatus[] } = createTemplate(published);
			blogMutation.mutate({ ..._template });
			return;
		}

		const templateCreation = createTemplate(published);
		const template: { template: Api.IBlogTemplate; statuses: Api.ITemplateStatus[]; id: string } = {
			...templateCreation,
			id: templateId,
		};
		blogUpdateMutation.mutate({ ...template });
		updateStatusesMutation.mutate({ statuses: template.statuses, templateId: template.id });
		if (templateCreation.statusesToAdd.length > 0) {
			addStatusesMutation.mutate({ statuses: templateCreation.statusesToAdd, templateId: template.id });
		}
		if (templateCreation.statusesToRemove.length > 0) {
			deleteStatusesMutation.mutate({ statuses: templateCreation.statusesToRemove, templateId: template.id });
		}
	};

	const onContentEditorStateChanged = (contentEditorState: Api.IRichContentEditorState) => {
		dispatch({
			type: REDUCER_TYPE_KEYS.PROMPT_INPUT_CHANGED,
			payload: contentEditorState,
		});
	};

	function onFinish() {
		if (locationState?.viewModel) {
			locationState.viewModel.load()?.catch((err: Api.IOperationResultNoValue) => {
				logApiError('RefreshTemplateCategory-Error', err);

				errorMessages.pushApiError(err);
			});
		}

		parentModal?.onRequestClose(null, false);
	}

	return (
		<div className={`${className} ${css(styleSheet.container)}`}>
			<div className={css(styleSheet.innerContainer)}>
				<MultiContainerHeader
					fullscreenHeader={`${templateId ? (isReadOnly ? 'View' : 'Edit') : 'Create New'} Blog Template`}
				/>

				<h1 className={css(styleSheet.mainTitle)}>
					{templateId ? (isReadOnly ? 'View' : 'Edit') : 'Create New'} Blog Template
				</h1>

				<div className={css(styleSheet.formControlWrap)}>
					<label htmlFor='aiPrompt'>Writing Instructions for the AI:</label>
					<RichContentDocumentEditor
						className={css(styleSheet.prompt)}
						config={isReadOnly ? defaulReadOnlyEditorConfig : defaultEditorConfig}
						contentState={state.promptInput}
						onContentStateChanged={onContentEditorStateChanged}
						readOnly={isReadOnly}
					/>
					{!isReadOnly ? (
						<button
							type='button'
							disabled={state.generatingBlogLoading}
							className={css(
								baseStyleSheet.ctaButton,
								state.content ? baseStyleSheet.ctaSuccessButton : undefined,
								styleSheet.generate
							)}
							onClick={generateContent}
						>
							{state.generatingBlogLoading ? (
								<LoadingSpinner type='tiny' />
							) : (
								<>
									{state.content ? (
										<RefreshIcon
											width={20}
											height={20}
											fillColor='#fff'
											hoverColor='#fff'
											className={css(styleSheet.refreshIcon)}
										/>
									) : null}{' '}
									{state.content ? 'Try Again' : 'Generate Blog Preview'}
								</>
							)}
						</button>
					) : null}
				</div>
				<div className={css(styleSheet.formControlWrap)}>
					{state.content ? <BlogPostContentPreview title={state.blogTitle} content={state.content} /> : null}
					{!state.imageSuggestingLoading ? (
						<>
							{!hasImageSelected ? (
								<>
									<label>Add Media</label>
									<ImageUploadButtons
										hasImage={hasImageSelected}
										onClickFreeImage={() => onClickFreeImage()}
										onClickMedia={onClickMedia}
									/>
								</>
							) : (
								<section>
									<h2 className={css(styleSheet.subTitle)}>
										{state.suggestedImage ? 'Ai suggested image' : 'Selected Image'}
									</h2>
									{state.selectedImage ? (
										<ImagePreview
											fileName={state.selectedImage?.fileName}
											fileSize={state.selectedImage?.fileSize}
											source={state.selectedImage?.source}
											onRemove={resetImage}
											url={state.selectedImage?.url}
										/>
									) : null}
								</section>
							)}
						</>
					) : (
						<section>
							<figure className={css(styleSheet.loadingImages)}>
								<LoadingSpinner type='large' />{' '}
								<figcaption style={{ marginLeft: 8 }}>Loading suggested images...</figcaption>
							</figure>
						</section>
					)}
				</div>
				<div className={css(styleSheet.formControlWrap)}>
					{blogCategoriesQuery.data ? (
						<IndustryCategorySection
							categoryOptions={blogCategoriesQuery.data}
							onCheckboxChanged={industry => selectedOption => onCheckboxChanged(industry)(selectedOption)}
							onSelectBoxChanged={industry => ev => onSelectBoxChanged(industry)(ev)}
							selectedOptions={state.selectedIndustryOptions}
							readOnly={isReadOnly}
						/>
					) : null}
				</div>
				<div className={css(styleSheet.formControlWrap)}>
					<div className={css(styleSheet.formControlWrapSpacers)}>
						<label htmlFor='title'>
							Blog Summary Title<span className='required'>*</span>
						</label>
						<TextInput
							disabled={isReadOnly}
							inputId='title'
							placeholder='Add a title that will appear in the blog summary… '
							type='text'
							value={state.titleInput}
							onChange={e => dispatch({ type: REDUCER_TYPE_KEYS.SUMMARY_INPUT_TITLE_CHANGED, payload: e.target.value })}
						/>
					</div>
					<div className={css(styleSheet.formControlWrapSpacers)}>
						<label htmlFor='summary'>
							Summary<span className='required'>*</span>
						</label>
						<TextArea
							styles={[styleSheet.summary]}
							disabled={isReadOnly}
							id='summary'
							placeholder='Add blog summary...'
							value={state.summaryInput}
							onChange={e =>
								dispatch({ type: REDUCER_TYPE_KEYS.SUMMARY_INPUT_DESCRIPTION_CHANGED, payload: e.target.value })
							}
						/>
					</div>
					<div className={css(styleSheet.formControlWrapSpacers)}>
						<label htmlFor='keywords'>
							Keywords<span className='required'>*</span>
						</label>
						<TextInput
							disabled={isReadOnly}
							inputId='keywords'
							onChange={e => dispatch({ type: REDUCER_TYPE_KEYS.KEYWORDS_INPUT_CHANGED, payload: e.target.value })}
							placeholder='Keywords (separated by comma):'
							type='text'
							value={state.keywordsInput}
						/>
					</div>
				</div>
				<footer className={css(styleSheet.footer)}>
					{isReadOnly ? null : (
						<>
							{!state.isPublished ? (
								<button type='button' className={css(baseStyleSheet.ctaButton)} onClick={() => handleSave()}>
									Save
								</button>
							) : null}
							<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={() => handleSave(true)}>
								Save &amp; Publish
							</button>
						</>
					)}
				</footer>
				<ImageFileChooserModal
					modalProps={{
						isOpen: state.fileInputModalOpen,
						onRequestClose: handleFileInputModalClose,
					}}
				/>

				<MediaChooserModal
					useBasicSearch={false}
					content={modalContent ?? undefined}
					modalProps={freeImageModalProps}
					imageOnly={true}
					styles={[styleSheet.mediaModalStyle]}
					onSave={onFreeImageChooserRequestClose}
				/>
			</div>
		</div>
	);
};

const EditBlogPostTemplateAsObserver = observer(_EditBlogPostTemplate);
export const EditBlogPostTemplate = inject(ModalChildComponentContextKey)(EditBlogPostTemplateAsObserver);
